/*
 * StackSchematic overlay: a Docs sidebar link that lands in the documentation,
 * not on a one-row index.
 *
 * WHY THIS EXISTS
 *
 * TechDocs' index page at /docs lists every entity that carries a
 * backstage.io/techdocs-ref. In this estate exactly one does - StackSchematic's
 * own Component, whose docs are the architecture, the decisions and the
 * runbooks. The household applications deliberately link OUT to their upstream
 * documentation rather than shipping TechDocs (docs/decisions/0016), so the
 * index is a single-row table that reads as empty while the real documentation
 * sits one unexplained click behind it. "The documentation page doesn't do
 * much" is exactly that page.
 *
 * So this link skips the picker. It DISCOVERS the documented entities at runtime
 * the way CatalogGraphNavItem discovers its roots - hardcoding no ref - and
 * hands them to docsHref, which lands the reader directly in the docs when there
 * is exactly one and degrades to the /docs index otherwise. In an install where
 * several things ship TechDocs, this becomes the upstream behaviour again on its
 * own.
 */
import { SidebarItem } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { useEffect, useState } from 'react';

import { docsHref, DOCS_INDEX } from './docsHref';

const TECHDOCS_REF = 'metadata.annotations.backstage.io/techdocs-ref';

/** Resolve where "Docs" should point by asking the catalog which entities have docs. */
function useDocsHref(): string {
  const catalogApi = useApi(catalogApiRef);
  const [href, setHref] = useState(DOCS_INDEX);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { items } = await catalogApi.getEntities({
        filter: { [TECHDOCS_REF]: CATALOG_FILTER_EXISTS },
        fields: ['kind', 'metadata.name', 'metadata.namespace'],
      });
      if (!cancelled) {
        setHref(docsHref(items));
      }
    })().catch(() => {
      // The index is the upstream landing: a worse page, not a broken one.
      // Failing to build the shortcut must leave the link working, not remove it.
    });

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return href;
}

export const DocsNavItem = () => {
  const to = useDocsHref();
  return <SidebarItem icon={MenuBookIcon} to={to} text="Docs" />;
};
